import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { Check } from "lucide-react"
import { cn } from "@/lib/utils"

interface CheckboxProps extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  variant?: "default" | "destructive"
  size?: "default" | "sm"
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, variant = "default", size = "default", ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer shrink-0 rounded bg-surface-input",
      size === "sm" ? "h-4 w-4 border" : "h-5 w-5 border-2",
      "transition-all duration-200 ease-in-out",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-dark",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "cursor-pointer",
      variant === "destructive"
        ? [
            "border-surface-border",
            "hover:border-red-400 hover:bg-surface-hover",
            "focus-visible:ring-red-500",
            "data-[state=checked]:bg-gradient-to-br data-[state=checked]:from-red-500 data-[state=checked]:to-red-600",
            "data-[state=checked]:border-red-500 data-[state=checked]:text-button-text",
            "data-[state=checked]:shadow-lg data-[state=checked]:shadow-red-500/30"
          ]
        : [
            "border-surface-border",
            "hover:border-primary/60 hover:bg-surface-hover",
            "focus-visible:ring-primary",
            "data-[state=checked]:bg-gradient-to-br data-[state=checked]:from-primary data-[state=checked]:to-primary/80",
            "data-[state=checked]:border-primary data-[state=checked]:text-button-text",
            "data-[state=checked]:shadow-lg data-[state=checked]:shadow-primary/30"
          ],
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn("flex items-center justify-center text-current")}
    >
      <Check className={cn(size === "sm" ? "h-3 w-3 stroke-[3]" : "h-3.5 w-3.5 stroke-[3]")} />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
